import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _ExpressionDomainModelImpl;
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { ArgumentOperandElementInfoImpl } from "abreact/expression/domain/ArgumentOperandElementInfoImpl";
import { ArgumentOperandImpl } from "abreact/expression/domain/ArgumentOperandImpl";
import { OperatorElementOptionImpl, ValueElementOptionImpl } from "abreact/expression/domain/ElementOptionImpl";
import { ValueCategoryAndTypeImpl } from "abreact/expression/domain/ValueCategoryAndTypeImpl";
import { ValueTypeWithCardinalityImpl } from "abreact/expression/domain/ValueTypeWithCardinalityImpl";
import { Expression } from "abreact/expression/Expression";
import { ExpArgumentOperandLabelPlacementEnum } from "abweb/generated/abwebinternal/api/dto/ExpArgumentOperand";
import { Assert } from "abweb/utils/Assert";
import { MapUtils } from "abweb/utils/MapUtils";
import { NullUtils } from "abweb/utils/NullUtils";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
export class ExpressionDomainModelImpl {
  constructor(expExpressionDomain, extendedExpExpressionDomains, categoryFilterRule, categorySorter, formulaElementOptionCreator) {
    _defineProperty(this, "_expressionDomain", void 0);
    _defineProperty(this, "_extendedDomains", void 0);
    _defineProperty(this, "supportsSubExpressions", void 0);
    _defineProperty(this, "_extendedDomainMap", new Map());
    _defineProperty(this, "_operatorRoleDefaultMap", new Map());
    _defineProperty(this, "_valueTypeMap", new Map());
    _defineProperty(this, "_fallbackValueTypeID", void 0);
    _defineProperty(this, "_expValueCategoriesByID", new Map());
    _defineProperty(this, "_expOperatorsByID", new Map());
    _defineProperty(this, "_expOperatorRolesByID", new Map());
    _defineProperty(this, "_expOperatorGroupsByID", new Map());
    _defineProperty(this, "_expArgumentOperandGroupsByID", new Map());
    _defineProperty(this, "_expExecutionFlagsByID", new Map());
    _defineProperty(this, "_expOperators", []);
    _defineProperty(this, "_expValueCategories", []);
    _defineProperty(this, "_expValueTypes", []);
    _defineProperty(this, "_expOperatorRoles", []);
    _defineProperty(this, "_expExecutionFlags", []);
    _defineProperty(this, "_defaultSourceOperandName", Expression.Domain.Constants.DEFAULT_SOURCE_VALUE_OPERAND_NAME);
    _defineProperty(this, "absentSentinelExpOperator", ObjectUtils.deepFreeze({
      id: "",
      label: Expression.Domain.Constants.ROLE_ABSENT,
      roleID: Expression.Domain.Constants.ROLE_ABSENT,
      sourceOperandName: this.defaultSourceOperandName,
      sourceOperandTypes: [{}],
      output: {
        categoryID: Expression.Domain.Constants.CATEGORY_LITERAL,
        valueTypeID: Expression.Domain.Constants.VALUE_TYPE_BOOLEAN
      }
    }));
    this._expressionDomain = expExpressionDomain;
    this._extendedDomains = extendedExpExpressionDomains;
    this._categoryFilterRule = categoryFilterRule;
    this._categorySorter = categorySorter ? categorySorter : this.getDefaultCategorySorter();
    this._formulaOptionCreator = formulaElementOptionCreator;
    this.initExtendedDomainMap();
    this.init(this._expressionDomain);
    this.initFinalLists();
    this.createMergedExpressionDomain();
    this.supportsSubExpressions = this.findExpValueCategory(Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION) != null;
  }
  createFilteredClone(categoryFilterRule) {
    const chainedFilter = category => {
      return !categoryFilterRule(category) ? false : this._categoryFilterRule ? this._categoryFilterRule(category) : true;
    };
    return new ExpressionDomainModelImpl(this._expressionDomain, this._extendedDomains, chainedFilter, this._categorySorter, this._formulaOptionCreator);
  }
  get id() {
    return this._expressionDomain.id;
  }
  get namespace() {
    return this._expressionDomain.namespace;
  }
  get version() {
    return this._expressionDomain.version;
  }
  get defaultSourceOperandName() {
    return this._defaultSourceOperandName;
  }
  get rootExpExpressionDomain() {
    return this._expressionDomain;
  }
  get mergedExpExpressionDomain() {
    return this._mergedExpressionDomain;
  }
  getExpValueTypes() {
    return this._expValueTypes;
  }
  findExpValueType(valueTypeID) {
    return this._valueTypeMap.get(valueTypeID);
  }
  getExpValueType(valueTypeID) {
    const expValueType = this.findExpValueType(valueTypeID);
    Assert.assert(expValueType != null, `Invalid valueTypeID: ${valueTypeID}`);
    return expValueType;
  }
  get fallbackValueTypeID() {
    return this._fallbackValueTypeID;
  }
  get fallbackValueTypeIDOrThrow() {
    Assert.assert(this._fallbackValueTypeID != null);
    return this._fallbackValueTypeID;
  }
  getExpValueCategories() {
    let expValueCategories = this._expValueCategories.slice().filter(cat => !cat.internalOnly);
    if (this._categoryFilterRule != null) {
      expValueCategories = expValueCategories.filter(cat => !this.isCategoryFilteredOut(cat));
    }
    if (this._categorySorter != null) {
      expValueCategories = expValueCategories.sort((cat1, cat2) => this._categorySorter(cat1, cat2));
    }
    return expValueCategories;
  }
  isCategoryFilteredOut(category) {
    return this._categoryFilterRule != null ? !this._categoryFilterRule(category) : false;
  }
  findExpValueCategory(valueCategoryID) {
    return this._expValueCategoriesByID.get(valueCategoryID);
  }
  getExpValueCategory(valueCategoryID) {
    const expValueCategory = this.findExpValueCategory(valueCategoryID);
    Assert.assert(expValueCategory != null, `Invalid valueCategoryID: ${valueCategoryID}`);
    return expValueCategory;
  }
  getExpExecutionFlags() {
    return this._expExecutionFlags;
  }
  findExpExecutionFlag(executionFlagId) {
    return this._expExecutionFlagsByID.get(executionFlagId);
  }
  getExpOperators() {
    return Array.from(this._expOperators);
  }
  getExpOperatorsWithExpOperatorRole(operatorRoleID) {
    return this.getExpOperators().filter(op => op.roleID === operatorRoleID);
  }
  findExpOperator(operatorID) {
    return operatorID === this.absentSentinelExpOperator.id ? this.absentSentinelExpOperator : this._expOperatorsByID.get(operatorID);
  }
  getExpOperator(operatorID) {
    const expOperator = this.findExpOperator(operatorID);
    Assert.assert(expOperator != null, `Invalid operatorID: ${operatorID}`);
    return expOperator;
  }
  findPlaceholderExpOperatorForExpOperatorRole(operatorRoleID) {
    const expOperatorRole = this.getExpOperatorRole(operatorRoleID);
    if (expOperatorRole.placeholderOperatorID != null) {
      return this.getExpOperator(expOperatorRole.placeholderOperatorID);
    }
  }
  isPlaceholderExpOperator(expOperator) {
    if (expOperator !== this.absentSentinelExpOperator) {
      const placeholderExpOperator = this.findPlaceholderExpOperatorForExpOperatorRole(expOperator.roleID);
      return placeholderExpOperator != null && expOperator.id === placeholderExpOperator.id;
    }
    return false;
  }
  determineOperatorApplicability(expOperator, valueCategoryAndType, ignoreOperatorCategory, operatorRoleID, qualifier, debug) {
    if (operatorRoleID != null && expOperator.roleID !== operatorRoleID) {
      if (!debug) {
        return [];
      }
      return [new OperatorElementOptionImpl(expOperator, valueCategoryAndType.valueCategoryID ?? "UNKNOWN", "UNKNOWN", false, undefined, [ExpressionDomainModelImpl.ROLE_NOT_APPLICABLE], false)];
    }
    const elementOptions = [];
    if (!expOperator.sourceOperandTypes) {
      return [];
    }
    for (const sourceOperandType of expOperator.sourceOperandTypes) {
      // Qualifiers must match. There is no wild-carding for qualifiers.
      if (qualifier !== sourceOperandType.qualifier) {
        continue;
      }
      for (const sourceOperandMetadata of this.normalizeValueCategoriesAndTypes(ignoreOperatorCategory ? undefined : sourceOperandType.categoryIDs, sourceOperandType.valueTypeIDs, sourceOperandType.multiple)) {
        for (const rule of ExpressionDomainModelImpl.operatorMatchingCategoryAndValueRuleList) {
          const operatorElementOption = rule(expOperator, valueCategoryAndType, sourceOperandMetadata, debug);
          if (operatorElementOption != null && (operatorElementOption.isValid || debug)) {
            elementOptions.push(operatorElementOption);
          }
        }
      }
    }
    return elementOptions;
  }
  getConnective(expOperator) {
    Assert.assert(expOperator.roleID === Expression.Domain.Constants.ROLE_JOIN);
    switch (expOperator.id) {
      case Expression.Domain.Constants.AND_OPERATOR_ID:
        return "AND";
      case Expression.Domain.Constants.OR_OPERATOR_ID:
        return "OR";
      default:
        throw new Error(`${expOperator.id} is an invalid connective.`);
    }
  }
  getExpOperatorRoles() {
    return Array.from(this._expOperatorRoles);
  }
  findExpOperatorRole(operatorRoleID) {
    return this._expOperatorRolesByID.get(operatorRoleID);
  }
  getExpOperatorRole(operatorRoleID) {
    const expOperatorRole = this.findExpOperatorRole(operatorRoleID);
    Assert.assert(expOperatorRole != null, `Invalid operatorRoleID: ${operatorRoleID}`);
    return expOperatorRole;
  }
  getExpOperatorGroups() {
    return Array.from(this._expOperatorGroupsByID.values());
  }
  findExpOperatorGroup(operatorGroupID) {
    return this._expOperatorGroupsByID.get(operatorGroupID);
  }
  getExpOperatorGroup(operatorGroupID) {
    const expOperatorGroup = this.findExpOperatorGroup(operatorGroupID);
    Assert.assert(expOperatorGroup != null, `Invalid operatorGroupID ${operatorGroupID}`);
    return expOperatorGroup;
  }
  getExpOperatorSummaryFormat(operatorID, summaryType) {
    let expOperatorSummaryFormat;
    const expOperator = this.getExpOperator(operatorID);
    if (expOperator.summaryOutputFormats) {
      expOperator.summaryOutputFormats.forEach(expOperatorSummaryFormatCandidate => {
        if (expOperatorSummaryFormatCandidate.summaryType === summaryType) {
          Assert.assert(expOperatorSummaryFormat == null, `Duplicate summary formats found with summaryType ${summaryType} on operator: ${operatorID}`);
          expOperatorSummaryFormat = expOperatorSummaryFormatCandidate;
        }
      });
    }
    return expOperatorSummaryFormat;
  }
  getExpArgumentOperandForExpOperator(operatorID, argumentOperandName) {
    let expArgumentOperand = this.findExpArgumentOperandForExpOperator(operatorID, argumentOperandName);
    Assert.assert(expArgumentOperand != null, `Argument operand with name ${argumentOperandName} not found on operator ${operatorID}`);
    return expArgumentOperand;
  }
  findExpArgumentOperandForExpOperator(operatorID, argumentOperandName) {
    const expOperator = this.getExpOperator(operatorID);
    let expArgumentOperand;
    if (expOperator.argumentOperands) {
      for (let expArgumentOperandCandidate of expOperator.argumentOperands) {
        if (expArgumentOperandCandidate.name === argumentOperandName) {
          expArgumentOperand = expArgumentOperandCandidate;
          break;
        }
      }
    }
    return expArgumentOperand;
  }
  getExpArgumentOperandGroup(operatorID, argumentOperandGroupID) {
    const expArgumentOperandGroup = this.findExpArgumentOperandGroup(operatorID, argumentOperandGroupID);
    Assert.assert(expArgumentOperandGroup != null, `Invalid argumentOperandGroupID for operator ${operatorID}: ${argumentOperandGroupID}`);
    return expArgumentOperandGroup;
  }
  findExpArgumentOperandGroup(operatorID, argumentOperandGroupID) {
    if (argumentOperandGroupID == null) {
      return undefined;
    }
    const expArgumentOperandGroupsByOperandName = this._expArgumentOperandGroupsByID.get(operatorID);
    if (expArgumentOperandGroupsByOperandName == null) {
      return undefined;
    }
    return expArgumentOperandGroupsByOperandName.get(argumentOperandGroupID);
  }
  getSourceOperandName(expOperator) {
    return expOperator.sourceOperandName;
  }
  determineApplicableOperatorsBasedOnAcceptedSourceValueTypes(valueCategoryID, valueTypeIDs, multiple, ignoreOperatorCategory, operatorRoleID, qualifier, debug) {
    const elementOptions = [];

    // These are all permutations of the source VALUE (left-hand-side) category and valueTypeIDs
    for (const sourceValueCategoryAndValueType of this.normalizeValueCategoriesAndTypes([valueCategoryID], valueTypeIDs.toArray(), multiple)) {
      for (const expOperator of this._expOperators) {
        if (expOperator.internalOnly) {
          continue;
        }
        if (this.isPlaceholderExpOperator(expOperator)) {
          continue;
        }
        this.determineOperatorApplicability(expOperator, sourceValueCategoryAndValueType, ignoreOperatorCategory, operatorRoleID, qualifier, debug).forEach(option => elementOptions.push(option));
      }
    }
    return this.consolidateOperatorElementOptions(elementOptions);
  }
  computeArgumentOperands(operatorID, sourceOperandValueCategoryID, sourceOperandValueTypeIDs, sourceOperandMultiple, operatorRoleIDs, selectAdvanced = false) {
    return this.computeArgumentOperandsInternal(true, operatorID, selectAdvanced, sourceOperandValueCategoryID, sourceOperandValueTypeIDs, sourceOperandMultiple, operatorRoleIDs);
  }
  computeArgumentOperandsNoOverloading(operatorID, operatorRoleIDs, selectAdvanced = false) {
    return this.computeArgumentOperandsInternal(false, operatorID, selectAdvanced, undefined, undefined, undefined, operatorRoleIDs);
  }
  determineApplicableOperatorElementOptionsForOutputType(valueTypeID, multiple, operatorRoleIDs) {
    const expOperators = this.determineApplicableExpOperatorsForOutputType(valueTypeID, multiple, operatorRoleIDs?.toArray());
    return expOperators.map(expOperator => new OperatorElementOptionImpl(expOperator, expOperator.output.categoryID, valueTypeID, multiple));
  }
  computeArgumentOperandsInternal(allowOverloading, operatorID, selectAdvanced, sourceValueCategoryID, sourceValueTypeIDs, sourceMultiple, operatorRoleIDs) {
    const groupedArguments = new Map();
    const groupedInfos = new Map();
    const bestFunctionInfos = new Map();
    const expArgumentOperands = this.getExpOperator(operatorID).argumentOperands?.filter(expArgumentOperand => {
      return (expArgumentOperand.advanced ?? false) === selectAdvanced;
    });
    expArgumentOperands?.forEach(expArgumentOperand => {
      const expArgumentOperandGroup = expArgumentOperand.groupID ? this.getExpArgumentOperandGroup(operatorID, expArgumentOperand.groupID) : undefined;

      // create the map key as either the name of the group the arg belongs to or the args name
      const operandGroupIDOrOperandName = expArgumentOperand.groupID ? expArgumentOperand.groupID : expArgumentOperand.name;

      // check for an existing info
      const operand = groupedArguments.get(operandGroupIDOrOperandName);
      if (!operand) {
        groupedArguments.set(operandGroupIDOrOperandName, {
          group: expArgumentOperandGroup,
          arguments: [expArgumentOperand]
        });
        groupedInfos.set(operandGroupIDOrOperandName, []);
        bestFunctionInfos.set(operandGroupIDOrOperandName, new Map());
      } else {
        if (!!expArgumentOperand.advanced !== !!operand.arguments[0].advanced) {
          throw new Error(`Cannot mix advanced and non-advanced argument operands in same group: ${expArgumentOperand.name} and ${operandGroupIDOrOperandName}`);
        }
        operand.arguments.push(expArgumentOperand);
      }
      const elementInfos = groupedInfos.get(operandGroupIDOrOperandName) ?? [];
      let supportedValueCategoryAndTypeArray;
      if (allowOverloading) {
        // this will inflate or constrict the value types based on wildcards, specific values, allCategories flags etc.
        // these will all end up having categoryIDs
        supportedValueCategoryAndTypeArray = this.determineValueCategoryAndTypePermutationsForArgumentOperand(expArgumentOperand, allowOverloading, sourceValueCategoryID, sourceValueTypeIDs, sourceMultiple);
      } else {
        // this will inflate or constrict the value types based on wildcards, specific values, allCategories flags etc.
        supportedValueCategoryAndTypeArray = this.determineValueCategoryAndTypePermutationsForArgumentOperand(expArgumentOperand, allowOverloading);
      }
      if (!expArgumentOperand.onlyOperatorInput) {
        // Add a VALUE element option for each supported type (meaning the user can enter a value of this type, if supported by the UI)
        supportedValueCategoryAndTypeArray.forEach(supportedValueCategoryAndType => {
          // TODO: validate this somehow at time of loading the model? Perhaps have role indicate: supports overloading?
          if (!supportedValueCategoryAndType.valueCategoryID || !supportedValueCategoryAndType.valueTypeWithCardinality.valueTypeID || supportedValueCategoryAndType.valueTypeWithCardinality.multiple == null) {
            throw new Error(`Operators used in situations where there is no overloading must declare the category and valueType of their operands: ${operatorID} for argument ${expArgumentOperand.name}`);
          }
          elementInfos.push(new ArgumentOperandElementInfoImpl(operandGroupIDOrOperandName, expArgumentOperand, new ValueElementOptionImpl(this.getValueCategoryInternal(supportedValueCategoryAndType), this.getValueTypeInternal(supportedValueCategoryAndType), supportedValueCategoryAndType.valueTypeWithCardinality.multiple)));
        });
      }
      // Add OPERATOR options for operators that have the ability to produce a value matching one of the supported value types
      supportedValueCategoryAndTypeArray.forEach(supportedValueCategoryAndType => {
        // an operator might inherit noOperatorInput from global defaults, but onlyOperatorInput is
        // more specific, so it wins -- you can't set a global default of OnlyOperatorInput
        if (operatorRoleIDs && (!expArgumentOperand.noOperatorInput || expArgumentOperand.onlyOperatorInput)) {
          const functionExpOperators = this.determineApplicableExpOperatorsForOutputType(NullUtils.getRequiredPropertyValue(supportedValueCategoryAndType.valueTypeWithCardinality, "valueTypeID"), NullUtils.getRequiredPropertyValue(supportedValueCategoryAndType.valueTypeWithCardinality, "multiple"), operatorRoleIDs?.toArray());
          functionExpOperators.forEach(expOperator => {
            const bestSoFar = bestFunctionInfos.get(operandGroupIDOrOperandName)?.get(expOperator.id);
            // Set this as an option if there is no option there, or if it is a better match to the operand by category.
            if (bestSoFar == null || expOperator.output.categoryID !== bestSoFar.expArgumentOperand.categoryID && expOperator.output.categoryID === expArgumentOperand.categoryID) {
              bestFunctionInfos.get(operandGroupIDOrOperandName)?.set(expOperator.id, new ArgumentOperandElementInfoImpl(operandGroupIDOrOperandName, expArgumentOperand, new OperatorElementOptionImpl(expOperator, expOperator.output.categoryID, NullUtils.getRequiredPropertyValue(supportedValueCategoryAndType.valueTypeWithCardinality, "valueTypeID"), NullUtils.getRequiredPropertyValue(supportedValueCategoryAndType.valueTypeWithCardinality, "multiple"))));
            }
          });
        }
      });
      if (this._formulaOptionCreator != null && !expArgumentOperand.noFormulaInput) {
        for (let supportedValueCategoryAndType of supportedValueCategoryAndTypeArray) {
          const formulaOption = this._formulaOptionCreator(expArgumentOperand, supportedValueCategoryAndType.valueCategoryIDOrThrow, NullUtils.getRequiredPropertyValue(supportedValueCategoryAndType.valueTypeWithCardinality, "valueTypeID"), NullUtils.getRequiredPropertyValue(supportedValueCategoryAndType.valueTypeWithCardinality, "multiple"));
          if (formulaOption && !this.isCategoryFilteredOut(formulaOption.expValueCategory)) {
            elementInfos.push(new ArgumentOperandElementInfoImpl(operandGroupIDOrOperandName, expArgumentOperand, formulaOption));
            break;
          }
        }
      }
    });
    for (const [operatorNameOrGroupId, functionInfos] of bestFunctionInfos) {
      for (const functionInfo of functionInfos.values()) {
        groupedInfos.get(operatorNameOrGroupId)?.push(functionInfo);
      }
    }
    return Array.from(MapUtils.mapValues(groupedArguments, (value, key) => {
      return new ArgumentOperandImpl(value.arguments, value.group, groupedInfos.get(key) ?? []);
    }).values());
  }
  normalizeValueTypes(valueTypeIDs, multiple) {
    const typeIds = valueTypeIDs && valueTypeIDs.length > 0 ? valueTypeIDs : [undefined];
    const multiples = multiple != null ? [multiple] : [true, false];
    const valueTypes = [];
    for (const possibleTypeId of typeIds) {
      for (const possibleMultiple of multiples) {
        valueTypes.push(new ValueTypeWithCardinalityImpl(possibleTypeId, possibleMultiple));
      }
    }
    return valueTypes;
  }
  normalizeValueCategoriesAndNormalizedTypes(valueCategoryIDs, valueTypes) {
    const categories = valueCategoryIDs && valueCategoryIDs.length > 0 ? valueCategoryIDs : [undefined];
    const types = valueTypes && valueTypes.length > 0 ? valueTypes : [new ValueTypeWithCardinalityImpl(undefined, undefined)];
    const valueCategoryAndTypeArray = [];
    for (const category of categories) {
      for (const valueType of types) {
        valueCategoryAndTypeArray.push(new ValueCategoryAndTypeImpl(category, valueType.valueTypeID, valueType.multiple));
      }
    }
    return valueCategoryAndTypeArray;
  }
  normalizeValueCategoriesAndTypes(valueCategoryIDs, valueTypeIDs, multiple) {
    return this.normalizeValueCategoriesAndNormalizedTypes(valueCategoryIDs, this.normalizeValueTypes(valueTypeIDs, multiple));
  }
  init(domain) {
    let domainExtension = domain.extendsDomain;
    if (domainExtension) {
      let extendedDomain = this._extendedDomainMap.get(domainExtension.expressionDomain);
      if (!extendedDomain) {
        throw new Error(`Extended domain ${domainExtension.expressionDomain} not found for domain: ${domain.id}`);
      }
      this.init(extendedDomain);
    }
    this.initOperatorRoles(domain);
    this.initOperatorGroups(domain);
    this.initValueTypes(domain);
    this.initCategories(domain);
    this.initGlobalOptions(domain);
    this.initOperators(domain);
    this.initExecutionFlags(domain);
  }
  initGlobalOptions(domain) {
    if (domain.globalOptions) {
      if (domain.globalOptions.sourceOperandName) {
        this._defaultSourceOperandName = domain.globalOptions.sourceOperandName;
      }
      if (domain.globalOptions.operatorRoleDefaults) {
        domain.globalOptions.operatorRoleDefaults.forEach(operatorRoleDefaults => {
          if (!this._expOperatorRolesByID.get(operatorRoleDefaults.roleID)) {
            throw new Error(`Invalid Role ID specified in globalOptions operatorRoleOutputDefaults in domain ${domain.id}: ${operatorRoleDefaults.roleID}`);
          }
          this.validateOperatorRoleDefaults(operatorRoleDefaults, domain);
          this._operatorRoleDefaultMap.set(operatorRoleDefaults.roleID, operatorRoleDefaults);
        });
      }
    }
  }
  validateOperatorRoleDefaults(operatorRoleDefaults, domain) {
    if (operatorRoleDefaults.outputCategoryID) {
      if (!this._expValueCategoriesByID.get(operatorRoleDefaults.outputCategoryID)) {
        throw new Error(`ERROR: Invalid Category ID specified as outputCategoryID in domain ${domain.id}: roleID: ${operatorRoleDefaults.roleID} ${operatorRoleDefaults.outputCategoryID}`);
      }
    }
    if (operatorRoleDefaults.defaultOperandCategoryID) {
      if (!this._expValueCategoriesByID.get(operatorRoleDefaults.defaultOperandCategoryID)) {
        throw new Error(`ERROR: Invalid Category ID specified as defaultOperandCategoryID in domain ${domain.id}: roleID: ${operatorRoleDefaults.roleID} ${operatorRoleDefaults.defaultOperandCategoryID}`);
      }
    }
    if (operatorRoleDefaults.outputValueTypeID) {
      if (!this._valueTypeMap.get(operatorRoleDefaults.outputValueTypeID)) {
        throw new Error(`ERROR: Invalid Expression.Domain.ValueTypeWithCardinality ID specified as role outputValueTypeID in domain ${domain.id}: roleID: ${operatorRoleDefaults.roleID} ${operatorRoleDefaults.outputValueTypeID}`);
      }
    }
  }
  initFinalLists() {
    this._expOperatorsByID.forEach(op => this._expOperators.push(op));
    this._expOperatorRolesByID.forEach(role => this._expOperatorRoles.push(role));
    this._valueTypeMap.forEach(vt => this._expValueTypes.push(vt));
    this._expValueCategoriesByID.forEach(vc => this._expValueCategories.push(vc));
    this._expExecutionFlagsByID.forEach(flag => this._expExecutionFlags.push(flag));
    this._expValueCategories = this._expValueCategories.sort((o1, o2) => this.compareNames(o1.id, o2.id));
    this._expOperators = this._expOperators.sort((o1, o2) => this.compareNames(o1.id, o2.id));
    this._expValueTypes = this._expValueTypes.sort((o1, o2) => this.compareNames(o1.id, o2.id));
    this._expOperatorRoles = this._expOperatorRoles.sort((o1, o2) => this.compareNames(o1.id, o2.id));
    // Sort by sort priority desc (i.e. higher sort priority shows up first).
    this._expExecutionFlags = this._expExecutionFlags.sort((o1, o2) => (o2.sortPriority ?? 0) - (o1.sortPriority ?? 0));
  }
  createMergedExpressionDomain() {
    const expressionDomain = this._expressionDomain;
    this._mergedExpressionDomain = {
      id: expressionDomain.id,
      description: expressionDomain.description,
      label: expressionDomain.label,
      namespace: expressionDomain.namespace,
      version: expressionDomain.version,
      editorConfiguration: expressionDomain.editorConfiguration,
      executionFlags: expressionDomain.executionFlags,
      fallbackValueTypeID: expressionDomain.fallbackValueTypeID,
      globalOptions: expressionDomain.globalOptions,
      labeledOperatorGroups: expressionDomain.labeledOperatorGroups,
      anonymousOperatorGroups: expressionDomain.anonymousOperatorGroups
    };
    this._mergedExpressionDomain.roles = this._expOperatorRoles;
    this._mergedExpressionDomain.operators = this._expOperators;
    this._mergedExpressionDomain.valueTypes = this._expValueTypes;
    this._mergedExpressionDomain.categories = this._expValueCategories;
    this._mergedExpressionDomain.globalOptions = this._expressionDomain.globalOptions || {};
    this._mergedExpressionDomain.globalOptions.operatorRoleDefaults = [];
    this._mergedExpressionDomain.labeledOperatorGroups = this._expressionDomain.labeledOperatorGroups;
    this._mergedExpressionDomain.anonymousOperatorGroups = this._expressionDomain.anonymousOperatorGroups;
    this._mergedExpressionDomain.executionFlags = this._expressionDomain.executionFlags;
    this._operatorRoleDefaultMap.forEach(defaults => {
      this._mergedExpressionDomain.globalOptions.operatorRoleDefaults.push(defaults);
    });
  }
  initExtendedDomainMap() {
    if (this._extendedDomains) {
      this._extendedDomains.forEach(domain => {
        if (this._extendedDomainMap.get(domain.id)) {
          throw new Error("Duplicated definition of an extended domain: " + domain.id);
        } else {
          this._extendedDomainMap.set(domain.id, domain);
        }
      });
    }
  }
  compareNames(n1, n2) {
    if (n2.indexOf("_") === 0) {
      return -1;
    } else if (n1.indexOf("_") === 0) {
      return 1;
    } else {
      return n1.localeCompare(n2);
    }
  }
  initValueTypes(domain) {
    if (domain.valueTypes) {
      let domainValueTypes = new Map();
      domain.valueTypes.forEach(vt => {
        if (domainValueTypes.get(vt.id)) {
          throw new Error(`Multiple definitions of Expression.Domain.ValueTypeWithCardinality in domain ${domain.id}: ${vt.id}`);
        }
        domainValueTypes.set(vt.id, vt);
        this._valueTypeMap.set(vt.id, vt);
      });
    }
    this._fallbackValueTypeID = domain.fallbackValueTypeID;
  }
  initCategories(domain) {
    if (domain.categories) {
      let domainCategories = new Map();
      domain.categories.forEach(cat => {
        if (domainCategories.get(cat.id)) {
          throw new Error(`Multiple definitions of Category in domain ${domain.id}: ${cat.id}`);
        }
        this._expValueCategoriesByID.set(cat.id, cat);
        domainCategories.set(cat.id, cat);
      });
    }
  }
  initOperatorRoles(domain) {
    if (domain.roles) {
      let domainOperatorRoles = new Map();
      domain.roles.forEach(role => {
        if (domainOperatorRoles.get(role.id)) {
          throw new Error(`Multiple definitions of Operator Role in domain ${domain.id}: ${role.id}`);
        }
        this._expOperatorRolesByID.set(role.id, role);
        domainOperatorRoles.set(role.id, role);
      });
    }
  }
  initOperatorGroups(expExpressionDomain) {
    const operatorGroups = [];
    if (expExpressionDomain.anonymousOperatorGroups != null) {
      operatorGroups.push(...expExpressionDomain.anonymousOperatorGroups);
    }
    if (expExpressionDomain.labeledOperatorGroups != null) {
      operatorGroups.push(...expExpressionDomain.labeledOperatorGroups);
    }

    /**
     * Record all known {@link Expression.Domain.ExpOperatorGroup}s in {@link _expOperatorGroupsByID}, verifying that no duplicates
     * exist.
     */
    for (const expOperatorGroup of operatorGroups) {
      if (this._expOperatorGroupsByID.has(expOperatorGroup.id)) {
        throw new Error(`Multiple definitions of Expression.Domain.ExpOperatorGroup "${expOperatorGroup.id}" in domain "${expExpressionDomain.id}"`);
      }
      this._expOperatorGroupsByID.set(expOperatorGroup.id, expOperatorGroup);
    }

    /**
     * Then, for each {@link Expression.Domain.ExpOperatorGroup}, walk its ${@link Expression.Domain.ExpOperatorGroup#groupID} chain to verify that
     * all specified {@link Expression.Domain.ExpOperatorGroup} hierarchies are valid and do not involve self-containment.
     */
    for (let expOperatorGroup of operatorGroups) {
      const operatorGroupIDs = [];
      while (expOperatorGroup.groupID != null) {
        operatorGroupIDs.push(expOperatorGroup.id);
        if (operatorGroupIDs.includes(expOperatorGroup.groupID)) {
          const hierarchy = operatorGroupIDs.map(groupID => `"${groupID}"`).join(" < ");
          throw new Error(`Self-containment is not allowed within an Expression.Domain.ExpOperatorGroup hierarchy: ${hierarchy}`);
        }
        const parentExpOperatorGroup = this._expOperatorGroupsByID.get(expOperatorGroup.groupID);
        if (parentExpOperatorGroup == null) {
          throw new Error(`Invalid parent Expression.Domain.ExpOperatorGroup "${expOperatorGroup.groupID}" specified on Expression.Domain.ExpOperatorGroup "${expOperatorGroup.id}" in domain "${expExpressionDomain.id}"`);
        }
        expOperatorGroup = parentExpOperatorGroup;
      }
    }
  }

  /**
   * Validates that an argument operand or group dependency is properly configured and does not create
   * circular or invalid dependencies within an operator's argument structure.
   * 
   * This method enforces dependency validation rules to ensure that:
   * - Dependencies are properly specified (either operandName OR groupID, but not both or neither)
   * - No circular dependencies exist (operands/groups cannot depend on themselves)
   * - Dependencies don't create conflicts within the same argument group
   * 
   * @param dependency The dependency configuration to validate
   * @param operatorId The ID of the operator containing this dependency, used for error messages and lookups
   * @param operand The argument operand that declares this dependency, or undefined if this is a group dependency
   * @param group The argument operand group that declares this dependency, or undefined if this is an operand dependency
   */
  validateDependency(dependency, operatorId, operand, group) {
    const owner = operand != null ? `argument operand "${operand.name}"` : `group "${group?.id}"`;
    if (dependency.operandName == null && dependency.groupID == null) throw new Error(`Invalid dependency declared for ${owner}. Must specify an operand name or a group ID.`);
    if (dependency.operandName != null && dependency.groupID != null) throw new Error(`Invalid dependency declared for ${owner}. Must specify only one of operand name or group ID.`);
    if (operand != null) {
      if (dependency.operandName === operand.name) throw new Error(`Invalid dependency declared for ${owner}. Cannot depend on self.`);
      if (dependency.groupID != null && dependency.groupID === operand.groupID) throw new Error(`Invalid dependency declared for ${owner}. Cannot depend on same group as argument.`);
      if (dependency.operandName != null && operand.groupID != null && this.getExpArgumentOperandForExpOperator(operatorId, dependency.operandName).groupID === operand.groupID) throw new Error(`Invalid dependency declared for ${owner}. Cannot depend on an argument in the same group as argument.`);
    }
    if (group != null) {
      if (dependency.groupID === group.id) throw new Error(`Invalid dependency declared for ${owner}. Cannot depend on self.`);
      if (dependency.operandName != null && this.getExpArgumentOperandForExpOperator(operatorId, dependency.operandName).groupID === group.id) throw new Error(`Invalid dependency declared for ${owner}. Cannot depend on an argument in the same group.`);
    }
  }
  initOperators(domain) {
    if (domain.operators) {
      let domainOperators = new Map();
      domain.operators.forEach(operator => {
        if (domainOperators.get(operator.id)) {
          console.log(`WARNING: multiple definitions of Operator in domain ${domain.id}: ${operator.id}`);
        }
        operator.sourceOperandTypes && operator.sourceOperandTypes.forEach(sourceOperandType => {
          sourceOperandType.valueTypeIDs && sourceOperandType.valueTypeIDs.forEach(vtn => {
            if (!this._valueTypeMap.get(vtn)) {
              throw new Error(`ERROR: Invalid Source Operand Value Type specified on Operator in domain ${domain.id}: ${operator.id}: ${vtn}`);
            }
          });
          sourceOperandType.categoryIDs && sourceOperandType.categoryIDs.forEach(vtn => {
            if (!this._expValueCategoriesByID.get(vtn)) {
              throw new Error(`ERROR: Invalid Source Operand Category Value specified on Operator in domain ${domain.id}: ${operator.id}: ${vtn}`);
            }
          });
        });
        const validatedOperator = this.initOperatorFromDefaults(operator, domain);
        operator.argumentOperandGroups && operator.argumentOperandGroups.forEach(group => {
          let groupsForOperator = this._expArgumentOperandGroupsByID.get(operator.id);
          if (!groupsForOperator) {
            groupsForOperator = new Map();
            this._expArgumentOperandGroupsByID.set(operator.id, groupsForOperator);
          }
          if (groupsForOperator.get(group.id)) {
            throw new Error(`ERROR: Duplicate Argument Operand Group specified on Operator in domain ${domain.id}: ${operator.id}: ${group.id}`);
          }
          groupsForOperator.set(group.id, group);
        });
        if (operator.roleID && !this._expOperatorRolesByID.get(operator.roleID)) {
          throw new Error(`ERROR: Invalid Operator Role specified on Operator in domain ${domain.id}: ${operator.id}: ${operator.roleID}`);
        }
        if (operator.groupID != null) {
          this.getExpOperatorGroup(operator.groupID);
        }
        let seenNames = [];
        operator.argumentOperands && operator.argumentOperands.forEach(expArgumentOperand => {
          if (expArgumentOperand.groupID) {
            let groupsForOperator = this._expArgumentOperandGroupsByID.get(operator.id);
            if (!groupsForOperator || !groupsForOperator.get(expArgumentOperand.groupID)) {
              throw new Error(`ERROR: Invalid Operand Group specified on Operator Operand in domain ${domain.id}: ${operator.id}: ${expArgumentOperand.name} in ${expArgumentOperand.groupID}`);
            }
          }
          if (expArgumentOperand.valueTypeID && !this._valueTypeMap.get(expArgumentOperand.valueTypeID)) {
            throw new Error(`ERROR: Invalid valueTypeID Type specified on Operator Operand in domain ${domain.id}: ${operator.id} : ${expArgumentOperand.name} with ${expArgumentOperand.valueTypeID}`);
          }
          if (expArgumentOperand.categoryID && !this._expValueCategoriesByID.get(expArgumentOperand.categoryID)) {
            throw new Error(`ERROR: Invalid Argument Operand Category specified in domain ${domain.id} on Operator.Operand ${operator.id}.${expArgumentOperand.name}: ${expArgumentOperand.categoryID}`);
          }
          if (expArgumentOperand.defaultCategoryID) {
            if (!this._expValueCategoriesByID.get(expArgumentOperand.defaultCategoryID)) {
              throw new Error(`ERROR: Invalid Category ID specified as defaultCategoryID domain ${domain.id}: ${operator.id}.${expArgumentOperand.name}: ${expArgumentOperand.defaultCategoryID}`);
            }
          }
          if (seenNames.indexOf(expArgumentOperand.name) > -1) {
            throw new Error(`ERROR: Duplicated Argument Operand name on Operator in domain ${domain.id}: ${operator.id}: ${expArgumentOperand.name}`);
          } else {
            seenNames.push(expArgumentOperand.name);
          }
          this.initArgumentOperandRestrictions(domain, validatedOperator, expArgumentOperand);
          this.initOperatorOperandFromDefaults(validatedOperator, expArgumentOperand);
        });
        this._expOperatorsByID.set(operator.id, validatedOperator);
        domainOperators.set(operator.id, validatedOperator);

        // Validate argument operand dependencies
        operator.argumentOperandGroups?.forEach(group => {
          group.dependencies?.forEach(dependency => {
            this.validateDependency(dependency, operator.id, undefined, group);
          });
        });
        operator.argumentOperands?.forEach(operand => {
          operand.dependencies?.forEach(dependency => {
            this.validateDependency(dependency, operator.id, operand, undefined);
          });
        });
      });
    }
  }
  initExecutionFlags(domain) {
    let executionFlags = new Set();
    domain.executionFlags?.forEach(flag => {
      if (executionFlags.has(flag.id)) {
        throw new Error(`Multiple definitions of ExecutionFlag in domain ${domain.id}: ${flag.id}`);
      }
      executionFlags.add(flag.id);
      this._expExecutionFlagsByID.set(flag.id, flag);
    });
  }
  initArgumentOperandRestrictions(expExpressionDomain, expOperator, expArgumentOperand) {
    if (expArgumentOperand.restrictions != null) {
      if (!expArgumentOperand.expandToAllDefaultCategories) {
        throw new Error(`ERROR: Restrictions cannot be specified on Argument Operand where expandToAllDefaultCategories is false: ${expExpressionDomain.id}: ${expOperator.id}: ${expArgumentOperand.name}.`);
      }
      expArgumentOperand.restrictions.forEach(restriction => {
        if (restriction.forValueTypeID == null || restriction.omitCategoryID == null) {
          throw new Error(`ERROR: Invalid Argument Operand Restriction specified on Operator Operand in domain ${expExpressionDomain.id}: ${expOperator.id}: ${expArgumentOperand.name}. Both forValueType and omitCategoryID must be specified.`);
        }
        if (!this._valueTypeMap.get(restriction.forValueTypeID)) {
          throw new Error(`ERROR: Invalid valueTypeID Type specified on Operator Operand Restriction in domain ${expExpressionDomain.id}: ${expOperator.id} : ${expArgumentOperand.name} with ${restriction.forValueTypeID}`);
        }
        if (!this._expValueCategoriesByID.get(restriction.omitCategoryID)) {
          throw new Error(`ERROR: Invalid Argument Operand Category specified on Operator Operand ${expOperator.id}.${expArgumentOperand.name} in domain ${expExpressionDomain.id}: ${expOperator.id}: ${restriction.omitCategoryID}`);
        }
      });
    }
  }
  initOperatorOperandFromDefaults(expOperator, expArgumentOperand) {
    let defaults = this._operatorRoleDefaultMap.get(expOperator.roleID);
    /*
    If the operand declares its own numDecimalPlaces value, then respect that. Otherwise, default
    from its valueType, if that provides a value.
     */
    if (expArgumentOperand.numDecimalPlaces == null && expArgumentOperand.valueTypeID) {
      expArgumentOperand.numDecimalPlaces = this.getExpValueType(expArgumentOperand.valueTypeID).numDecimalPlaces;
    }
    /*
    If a label is provided but there is no label placement specified, default it to PREFIX
     */
    if (expArgumentOperand.label && !expArgumentOperand.labelPlacement) {
      expArgumentOperand.labelPlacement = ExpArgumentOperandLabelPlacementEnum.PREFIX;
    }
    if (defaults) {
      if (expArgumentOperand.noFormulaInput == null) {
        expArgumentOperand.noFormulaInput = defaults.noFormulaInput;
      }
      if (expArgumentOperand.noOperatorInput == null) {
        expArgumentOperand.noOperatorInput = defaults.noOperatorInput;
      }
      if (expArgumentOperand.defaultCategoryID == null) {
        expArgumentOperand.defaultCategoryID = defaults.defaultOperandCategoryID;
      }
    }
  }
  initOperatorFromDefaults(operator, domain) {
    let defaults = this._operatorRoleDefaultMap.get(operator.roleID);
    if (!operator.sourceOperandName) {
      let sourceOperandName;
      if (defaults?.sourceOperandName) {
        sourceOperandName = defaults.sourceOperandName;
      } else {
        sourceOperandName = this._defaultSourceOperandName;
      }
      operator.sourceOperandName = sourceOperandName;
    }
    const operatorRoleDefault = this._operatorRoleDefaultMap.get(operator.roleID);
    let operatorOutput = operator.output;
    if (operatorOutput) {
      if (operatorOutput.categoryID) {
        if (!this._expValueCategoriesByID.get(operatorOutput.categoryID)) {
          throw new Error(`ERROR: Invalid Category ID specified on Operator output in domain ${domain.id}: ${operator.id}: ${operatorOutput.categoryID}`);
        }
      } else if (operatorRoleDefault?.outputCategoryID != null) {
        operatorOutput.categoryID = operatorRoleDefault.outputCategoryID;
      } else {
        throw new Error(`ERROR: No Output valueCategoryID specified for Operator and no default provided for role ${operator.roleID} in domain ${domain.id}: ${operator.id}`);
      }
      if (!this._valueTypeMap.get(operatorOutput.valueTypeID)) {
        throw new Error(`ERROR: Invalid Expression.Domain.ValueTypeWithCardinality ID specified on Operator output in domain ${domain.id}: ${operator.id}: ${operatorOutput.valueTypeID}`);
      }
    } else if (operatorRoleDefault) {
      if (operatorRoleDefault?.outputCategoryID != null && operatorRoleDefault.outputValueTypeID != null) {
        operatorOutput = {
          categoryID: operatorRoleDefault.outputCategoryID,
          valueTypeID: operatorRoleDefault.outputValueTypeID,
          multiple: operatorRoleDefault.outputMultiple ?? false
        };
        operator.output = operatorOutput;
      }
    }
    if (!operator.output) {
      throw new Error(`ERROR: No Output specified for Operator and defaults not provided for role ${operator.roleID} in domain ${domain.id}: ${operator.id}`);
    }
    return operator;
  }
  getValueCategoryInternal(valueCategoryAndType) {
    return this.getExpValueCategory(NullUtils.getRequiredPropertyValue(valueCategoryAndType, "valueCategoryID"));
  }
  getValueTypeInternal(valueCategoryAndType) {
    return this.getExpValueType(NullUtils.getRequiredPropertyValue(valueCategoryAndType.valueTypeWithCardinality, "valueTypeID"));
  }

  /*
  Operators may have been matched due to multiple source value types (equals will work for both string and ssn),
  So they need to be consolidated to a single element representing that operator with multiple inclusion messages
   */
  consolidateOperatorElementOptions(elementOptions) {
    let operatorToOptionMap = new Map();
    let consolidateOptions = [];

    // consolidate the valid ones. No need to consolidate invalid -- they already have been consolidated by framework
    elementOptions.filter(elementOption => elementOption.isValid).forEach(elementOption => {
      let existingOption = operatorToOptionMap.get(elementOption.expOperator.id);
      if (!existingOption) {
        operatorToOptionMap.set(elementOption.expOperator.id, elementOption);
        consolidateOptions.push(elementOption);
      } else {
        elementOption.inclusionMessages && elementOption.inclusionMessages.forEach(msg => {
          if (existingOption.inclusionMessages.indexOf(msg) < 0) {
            existingOption.inclusionMessages.push(msg);
          }
        });
      }
    });
    // now add the invalid ones
    elementOptions.filter(elementOption => !elementOption.isValid).forEach(elementOption => {
      consolidateOptions.push(elementOption);
    });
    return consolidateOptions;
  }
  calculatePossibleArgumentValueTypes(expArgumentOperand, allowOverloading, svValueTypeIDs, svMultiple) {
    // If the argOperand specifically declares a value type, then use that (doesn't matter what the source VALUE types are)
    // Otherwise, it has a wildcard for valueType, so it supports all the value types of the source VALUE
    const valueTypes = expArgumentOperand.valueTypeID ? [expArgumentOperand.valueTypeID] : svValueTypeIDs?.toArray();
    // If the argOperand specifically declares its multiplicity, then use that (doesn't matter what the source VALUE types are).
    // Otherwise, it has a wildcard for multiple, so it supports whatever the source VALUE is.
    // For backwards compatibility, if the argument operand does not specify multiple and we are not overloading
    // the source operand, assume multiple is false.
    const multiple = expArgumentOperand.multiple ?? (allowOverloading ? svMultiple : false);
    return this.normalizeValueTypes(valueTypes, multiple);
  }
  calculatePossibleArgumentValueCategories(expArgumentOperand, sourceValueCategoryID, sourceValueTypeIDs) {
    const valueCategoryIDs = [];
    // if the operand has a specific category name, then use it
    if (expArgumentOperand.categoryID) {
      valueCategoryIDs.push(expArgumentOperand.categoryID);
    }
    // if it declares that it supports all categories, then add them all
    else if (expArgumentOperand.expandToAllDefaultCategories) {
      this.getExpValueCategories().filter(cat => cat.includeInDefaultCategories).filter(cat => this.notOmittedByRestrictions(expArgumentOperand, cat, sourceValueTypeIDs)).forEach(cat => {
        if (!cat.internalOnly) {
          let catToAdd = cat.id;
          if (valueCategoryIDs.indexOf(catToAdd) === -1) {
            valueCategoryIDs.push(catToAdd);
          }
        }
      });
    }
    // if it does not declare a category and is not all categories, then it supports whatever the source VALUE category is
    else if (sourceValueCategoryID) {
      valueCategoryIDs.push(sourceValueCategoryID);
    }
    return valueCategoryIDs;
  }
  notOmittedByRestrictions(expArgumentOperand, expValueCategory, svValueTypeIDs) {
    let includeCategory = true;
    if (expArgumentOperand.restrictions && svValueTypeIDs) {
      for (let res of expArgumentOperand.restrictions) {
        if (res.omitCategoryID === expValueCategory.id && svValueTypeIDs.indexOf(res.forValueTypeID) > -1) {
          includeCategory = false;
        }
      }
    }
    return includeCategory;
  }
  determineValueCategoryAndTypePermutationsForArgumentOperand(expArgumentOperand, allowOverloading, svCategoryName, svValueTypeNames, svMultiple) {
    const valueTypes = this.calculatePossibleArgumentValueTypes(expArgumentOperand, allowOverloading, svValueTypeNames, svMultiple);
    const valueCategoryIDs = this.calculatePossibleArgumentValueCategories(expArgumentOperand, svCategoryName, svValueTypeNames);
    return this.normalizeValueCategoriesAndNormalizedTypes(valueCategoryIDs, valueTypes);
  }
  determineApplicableExpOperatorsForOutputType(valueTypeID, multiple, operatorRoleIds) {
    return this.getExpOperators().filter(expOperator => !expOperator.internalOnly).filter(expOperator => operatorRoleIds && operatorRoleIds.indexOf(expOperator.roleID) > -1 || !operatorRoleIds)
    // operator output is validated to be fully specified (using defaults if necessary) by domain model
    .filter(({
      output
    }) => {
      if (output.valueTypeID != null && output.valueTypeID !== valueTypeID) {
        return false;
      }
      return output.multiple == null || output.multiple === multiple;
    });
  }
  getDefaultCategorySorter() {
    return (cat1, cat2) => {
      let sortCat1 = cat1.sortPriority;
      let sortCat2 = cat2.sortPriority;
      sortCat1 = sortCat1 !== undefined ? sortCat1 : 0;
      sortCat2 = sortCat2 !== undefined ? sortCat2 : 0;
      return sortCat2 - sortCat1;
    };
  }
  static createOperatorMatchRules() {
    return [(operator, categoryAndValueType, operatorCatAndType, debug) => {
      return ExpressionDomainModelImpl.createOperatorElement(operator, categoryAndValueType, `Exact Match: operator ${operatorCatAndType} to ${categoryAndValueType}`, operatorCatAndType.exactMatch(categoryAndValueType), debug);
    }, (operator, categoryAndValueType, operatorCatAndType, debug) => {
      return ExpressionDomainModelImpl.createOperatorElement(operator, categoryAndValueType, `Partial Match: operator ${operatorCatAndType} to ${categoryAndValueType}`, operatorCatAndType.partialMatch(categoryAndValueType), debug);
    }];
  }
  static createOperatorElement(operator, valueCategoryAndType, message, valid, debug) {
    let elementOption = null;
    if (valid) {
      elementOption = new OperatorElementOptionImpl(operator, valueCategoryAndType.valueCategoryIDOrThrow, NullUtils.getRequiredPropertyValue(valueCategoryAndType.valueTypeWithCardinality, "valueTypeID"), NullUtils.getRequiredPropertyValue(valueCategoryAndType.valueTypeWithCardinality, "multiple"), [message]);
    } else if (debug) {
      elementOption = new OperatorElementOptionImpl(operator, valueCategoryAndType.valueCategoryID ?? "UNKNOWN", valueCategoryAndType.valueTypeWithCardinality.valueTypeID ?? "UNKNOWN", valueCategoryAndType.valueTypeWithCardinality.multiple ?? false, undefined, [`NO_${message}`], false);
    }
    return elementOption;
  }
}
_ExpressionDomainModelImpl = ExpressionDomainModelImpl;
_defineProperty(ExpressionDomainModelImpl, "ROLE_NOT_APPLICABLE", "ROLE_NOT_APPLICABLE");
_defineProperty(ExpressionDomainModelImpl, "operatorMatchingCategoryAndValueRuleList", _ExpressionDomainModelImpl.createOperatorMatchRules());
//# sourceMappingURL=ExpressionDomainModelImpl.js.map
